import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Guardian, Kid } from "../lib/types";

export type TabId = "cashier" | "whatsapp" | "parties" | "loyalty" | "admin" | "wallet" | "hr" | "hardware" | "downloads" | "pricing" | "staff" | "setup" | "customers" | "reports" | "expenses" | "receipts" | "shifts" | "einvoice" | "audit";

/** Values handed from another tab to pre-fill the check-in form (e.g. from Loyalty). */
export interface CheckInPrefill {
  guardian: Pick<Guardian, "id" | "fullName" | "phone">;
  kid: Pick<Kid, "id" | "name" | "stamps">;
  redeem?: boolean;
}

interface AppState {
  tab: TabId;
  setTab: (t: TabId) => void;
  prefill: CheckInPrefill | null;
  startCheckIn: (p: CheckInPrefill) => void;
  clearPrefill: () => void;
  /** Guardian whose chat the WhatsApp tab should open. */
  chatGuardianId: string | null;
  openChat: (guardianId: string) => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppStateProvider({ children, initialTab = "cashier" }: { children: ReactNode; initialTab?: TabId }) {
  const [tab, setTab] = useState<TabId>(initialTab);
  const [prefill, setPrefill] = useState<CheckInPrefill | null>(null);
  const [chatGuardianId, setChat] = useState<string | null>(null);

  const startCheckIn = useCallback((p: CheckInPrefill) => {
    setPrefill(p);
    setTab("cashier");
  }, []);
  const openChat = useCallback((id: string) => {
    setChat(id);
    setTab("whatsapp");
  }, []);

  const value = useMemo(
    () => ({ tab, setTab, prefill, startCheckIn, clearPrefill: () => setPrefill(null), chatGuardianId, openChat }),
    [tab, prefill, startCheckIn, chatGuardianId, openChat],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppState(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppState must be used inside <AppStateProvider>");
  return v;
}
